import { Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import NotificationStatusIcon from 'components/Notification/NotificationStatusIcon';

import { ICreateTask, ITask } from 'common/api/type-def';
import { ReactComponent as DeleteIcon } from 'common/assets/images/delete-icon.svg';
import { ReactComponent as EditIcon } from 'common/assets/images/edit-Icon.svg';
import SelectDropdown, {
	ISelectOption,
} from 'common/components/Select/SelectDropdown';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { MODULES } from 'common/constants';
import { PROGRESS_BAR_ELEMENTS } from 'common/constants/CitationDetails';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { ENTITY_TYPE } from 'common/constants/TaxonomyConstants';
import { LoggedInRole } from 'common/constants/UserManagementConstants';
import { RootState } from 'common/store/AppStore';
import {
	setRegInvententoryCreateDrawer,
	setRequirementInventoryBreadcrumbText,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { setSelectedGroupData } from 'common/store/reducer/regulationInventoryReducer';
import { setWorkflowFeedback } from 'common/store/reducer/simulationReducer';
import { isRoleExists } from 'common/utils/authCheck';
import { dateFullYearFormat, urlWithTenant } from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import {
	deleteGroupDataApi,
	updateBulkTask,
} from 'features/RegulationInventory/api';
import {
	IGroupsTableList,
	IRegInvOwnerInfo,
} from 'features/RegulationInventory/api/type-def';
import { getRegInvOwnerData } from 'features/RegulationInventory/RegInventoryUtils';
import RegulatoryInventoryAddGroup from 'features/RegulationInventory/RegulatoryInventoryAddGroup';

import styles from './GroupsTable.module.scss';

const { NOTSTARTED } = PROGRESS_BAR_ELEMENTS;

const { OK, BAD_REQUEST, NOT_FOUND, NO_CONTENT, CREATED } = HTTP_STATUS_CODES;

const {
	ROLE_OPERATIONS_MANAGER,
	ROLE_SYSTEM_ADMINISTRATOR,
	ROLE_USER_ADMINISTRATOR,
} = LoggedInRole;

const {
	regulationInventory,
	overView,
	regulatoryGroupReview,
	regulationSummaryAssessment,
} = rrcpDashboardRoutes;

interface IGroupsTableProps {
	id?: string;
	isLoading: boolean;
	inventoryTableData: any;
	getTableData: any;
	totalRecordsCount: number;
	selectedTabValue: string;
	errorMessage: string;
}
const GroupsTable = (props: IGroupsTableProps) => {
	const {
		id,
		isLoading,
		inventoryTableData,
		getTableData,
		totalRecordsCount,
		selectedTabValue,
		errorMessage,
	} = props;
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { tableQueryObj: tableProps, updateTableQuery: setTableProps } =
		useTableQuery({ id });
	const { updateQueryFlag } = useTableQuery({ id: 'AllRegTable' });
	const [buOwnerList, setBUOwnerList] = useState<IRegInvOwnerInfo[]>([]);
	const addGroupDrawerState = useSelector(
		(state: RootState) => state.common.regInvententoryCreateDrawer,
	);
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const { deleteIconFillColor } = styles;
	const checkRole = isRoleExists([
		ROLE_OPERATIONS_MANAGER,
		ROLE_SYSTEM_ADMINISTRATOR,
		ROLE_USER_ADMINISTRATOR,
	]);
	const onEditClick = (rowData: IGroupsTableList) => {
		dispatch(setSelectedGroupData(rowData));
		dispatch(setRegInvententoryCreateDrawer(true));
	};
	useEffect(() => {
		if (
			tableProps &&
			selectedTabValue === 'Grouping' &&
			addGroupDrawerState === false
		) {
			getTableData(tableProps);
		}
	}, [addGroupDrawerState]);
	const onDeleteClick = async (rowData: IGroupsTableList) => {
		const response: any = await deleteGroupDataApi(rowData, rowData.groupId);
		if (response?.statusCode === NO_CONTENT) {
			dispatch(
				setToastMessage({
					message: 'The Group deleted successfully',
					severity: 'success',
					isToastOpen: true,
				}),
			);
			getTableData(tableProps);
		}
		if (response?.errorBody || response?.error) {
			const { errorBody } = response;
			const responseError: any = response.error;
			const errorMsg =
				(errorBody?.errorMessage as string) ||
				(responseError?.message as string);
			dispatch(
				setToastMessage({
					message: errorMsg,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};
	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const navigationToGroupDetail = (params: any) => {
		dispatch(setWorkflowFeedback([]));
		updateQueryFlag(true);
		dispatch(
			setRequirementInventoryBreadcrumbText(MODULES.REQUIREMENT_INVENTORY),
		);
		if (selectedTabValue === 'Grouping') {
			navigate(
				urlWithTenant(
					`/${regulationInventory}/${overView}/${regulatoryGroupReview}/${params.groupId}`,
				),
			);
		} else if (selectedTabValue === 'Summary') {
			navigate(
				urlWithTenant(
					`/${regulationInventory}/${overView}/${regulationSummaryAssessment}/${params.groupId}`,
				),
			);
		}
	};

	const getOwnerData = async (status: string) => {
		if (buOwnerList.find((res) => res.status === status)) {
			return;
		}
		const tempData = await getRegInvOwnerData(status);
		if (tempData) {
			setBUOwnerList((prev) => [...prev, tempData]);
		}
	};

	const getGroupOwnerList = (status: string) => {
		let buOwnerValues: ISelectOption[] = [];
		buOwnerList.forEach((res) => {
			if (res.status === status) {
				buOwnerValues = res.owners;
			}
		});
		return buOwnerValues;
	};

	const onGroupOwnerChange =
		(params: IGroupsTableList) => (value: string, obj: any) => {
			const taskObj = {
				id: params.taskId,
				...(obj ? { ...obj } : {}),
				assignedObjectType: ENTITY_TYPE.GROUP,
				status: params.taskStatus,
				assignedObjectId: params.groupId,
			} as unknown as ITask;
			createUpdateGroupTask(taskObj);
		};

	const createUpdateGroupTask = async (taskObj: ITask) => {
		if (taskObj.id) {
			const response: any = await updateBulkTask([taskObj]);
			if (response.statusCode === OK || response.statusCode === CREATED) {
				updateTableData(response?.responseBody);
			}
			displayTaskMessage(response);
		}
	};
	const updateTableData = (response: ICreateTask) => {
		const taskList = response.taskResponseList || [];
		inventoryTableData.forEach((res: any) => {
			taskList.forEach((val: any) => {
				if (res.taskId === val.id) {
					res.assignedUserName = val.assignedUserFullName || '';
					res.assignedUserId = val.assignedUserId || '';
					res.taskId = val.id || '';
				}
			});
		});
	};
	const displayTaskMessage = (apiResponse: any) => {
		if (apiResponse) {
			if (apiResponse.statusCode === OK) {
				dispatch(
					setToastMessage({
						message: 'Details saved successfully',
						severity: 'success',
						isToastOpen: true,
					}),
				);
			}
			if (
				apiResponse.statusCode === BAD_REQUEST ||
				apiResponse.statusCode === NOT_FOUND
			) {
				if (apiResponse?.errorBody || apiResponse?.error) {
					const { errorBody } = apiResponse;
					const responseError: any = apiResponse.error;
					const errorMsg =
						(errorBody?.errorMessage as string) ||
						(responseError?.message as string);
					dispatch(
						setToastMessage({
							message: errorMsg,
							severity: 'error',
							isToastOpen: true,
						}),
					);
				}
			}
		}
	};

	const columns: readonly Column[] = [
		{
			id: 'groupName',
			label: 'Obligation',
			isSortable: true,
			disablePadding: false,
			cellClassName: styles['text-icon-wrpper'],
			render: (params: any) => (
				<div className={styles.gridContainer}>
					<Typography
						className={styles['text-section']}
						data-testid="theme-link"
						onClick={() => navigationToGroupDetail(params)}
					>
						<div className={styles['grid-wrapper']}>
							<Tooltip
								className={styles['text-value-section']}
								title={params.groupName}
							>
								<span>{params.groupName}</span>
							</Tooltip>
							<NotificationStatusIcon
								date={params?.dueDate}
								className={styles['icon-date']}
							/>
						</div>
					</Typography>
				</div>
			),
		},
		{
			id: 'description',
			label: 'Description',
			disablePadding: false,
			isSortable: true,
			render: (params: IGroupsTableList) => (
				<Typography className={styles['text-section-common']}>
					{params?.description}
				</Typography>
			),
		},
		{
			id: 'assignedUserName',
			label: 'Assigned To',
			disablePadding: false,
			isSortable: true,
			render: (params: IGroupsTableList) => (
				<Typography
					className={styles['text-section-common']}
					data-testid="Assigned-link"
				>
					{params?.assignedUserName}
				</Typography>
			),
			cellClassName: styles['owner-styling'],
			isCellHoverEditable: checkRole,
			renderHoverEditCell: (params: IGroupsTableList) => {
				if (params.taskStatus && params.taskStatus !== NOTSTARTED) {
					getOwnerData(params.taskStatus);
					return (
						<SelectDropdown
							label="Owner"
							name={`group-name-hover-${params.groupId}`}
							id="group-owner-hover"
							options={getGroupOwnerList(params.taskStatus)}
							mode="standard"
							showLabel={false}
							inputProps={{
								'data-testid': 'regulation-owner',
							}}
							value={params.assignedUserId}
							onChange={onGroupOwnerChange(params)}
							className={styles['select-regulation-owner']}
						/>
					);
				}
				return (
					<Typography className={styles['text-section-common']}>
						{params?.assignedUserName}
					</Typography>
				);
			},
			isCellEditable: checkRole,
			renderEditCell: (params: IGroupsTableList) => {
				if (params.taskStatus && params.taskStatus !== NOTSTARTED) {
					return (
						<SelectDropdown
							label="Owner name"
							name={`group-name-edit-${params.groupId}`}
							id="group-owner-edit"
							options={getGroupOwnerList(params.taskStatus)}
							mode="standard"
							showLabel={false}
							inputProps={{
								'data-testid': 'regulation-owner',
							}}
							value={params.assignedUserId}
							onChange={onGroupOwnerChange(params)}
							className={styles['select-regulation-owner']}
						/>
					);
				}
				return (
					<Typography className={styles['text-section-common']}>
						{params?.assignedUserName}
					</Typography>
				);
			},
		},
		{
			id: 'createdByName',
			label: 'Created By',
			disablePadding: false,
			isSortable: true,
			render: (params: IGroupsTableList) => (
				<Typography className={styles['text-section-common']}>
					{params?.createdByName}
				</Typography>
			),
		},
		{
			id: 'taskStatus',
			label: 'Status',
			disablePadding: false,
			isSortable: true,
			render: (params: IGroupsTableList) => (
				<Typography
					data-testid="group-status"
					className={styles['text-section-common']}
				>
					{params?.taskStatus || NOTSTARTED}
				</Typography>
			),
		},
		{
			id: 'priority',
			label: 'Priority',
			disablePadding: false,
			isSortable: true,
		},
		{
			id: 'reviewByDate',
			label: 'Due Date',
			disablePadding: false,
			isSortable: true,
			render: (params: IGroupsTableList) => (
				<Typography className={styles['text-section-common']}>
					{params?.reviewByDate ? dateFullYearFormat(params.reviewByDate) : ''}
				</Typography>
			),
		},
		{
			id: 'actions',
			label: 'Actions',
			disablePadding: false,
			isHidden: selectedTabValue !== 'Grouping',
			cellClassName: styles['action-styling'],
			isCellHoverEditable: true,
			renderHoverEditCell: (params: IGroupsTableList) => (
				<div>
					{loggedInUser?.userId === params.createdBy && (
						<>
							<EditIcon
								data-testid="edit-icon"
								className={styles['edit-delete-icon-actions']}
								onClick={() => onEditClick(params)}
							/>
							<DeleteIcon
								data-testid="delete-icon"
								className={styles['edit-delete-icon-actions']}
								onClick={() => onDeleteClick(params)}
								fill={deleteIconFillColor}
							/>
						</>
					)}
				</div>
			),
			render: () => <div />,
		},
	];

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			getTableData(data);
			setTableProps(data);
		}
	};

	const handleSortChange = (data: any) => {
		getTableData(data);
		setTableProps(data);
	};

	return (
		<Box data-testid="groups-main-box">
			<Box className={styles['table-area']}>
				<TableComponent
					defaultQuery={tableProps}
					isLoading={isLoading}
					rows={inventoryTableData}
					columns={columns}
					defaultSortKey="reviewByDate"
					sortDirection="desc"
					onPageHandler={handlePageChange}
					onSortHandler={handleSortChange}
					totalItems={totalRecordsCount}
					rowHoverEnabled
					enableHeaderColor
					customErrorMessage={errorMessage}
					rowKey="groupId"
					isRenderPagination
				/>
			</Box>

			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>

			{addGroupDrawerState && (
				<SideDrawer
					className={styles['groups-drawer']}
					drawerState={addGroupDrawerState}
				>
					<RegulatoryInventoryAddGroup />
				</SideDrawer>
			)}
		</Box>
	);
};
export default GroupsTable;
